import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Map, Check, Circle, Rocket } from "lucide-react";
import NavBar from "@/NavBar";
import Footer from "@/Footer";
import { LIVE_APPS, ROADMAP_APPS } from "@/apps";

const Row = ({ app, live }) => (
  <div className="hi-card p-5 flex items-start gap-4" data-testid={`roadmap-${app.id}`}>
    <img src={app.logo} alt={app.name} className="w-14 h-14 rounded-xl object-cover shrink-0" />
    <div className="min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <h3 className="hi-serif text-lg text-[#f3ead4]">{app.name}</h3>
        {live ? (
          <span className="hi-body text-[10px] font-bold uppercase tracking-wider text-[#7fe0a0] bg-[#1f7a45]/25 border border-[#39c274]/40 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
            <Check size={10} strokeWidth={3} /> Available now
          </span>
        ) : (
          <span className="hi-body text-[10px] font-bold uppercase tracking-wider text-[#8fbaff] bg-[#3b9bff]/12 border border-[#3b9bff]/30 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
            <Circle size={9} /> Planned
          </span>
        )}
      </div>
      <p className="hi-body text-xs uppercase tracking-[0.14em] mt-1" style={{ color: app.accent }}>
        {app.category} · {app.tagline}
      </p>
      <p className="hi-body text-sm text-white/70 mt-2">{app.description}</p>
      {!live && app.launch && (
        <p className="hi-body text-xs text-[#e0bb63] mt-2 font-semibold" data-testid={`launch-${app.id}`}>
          Target: {app.launch}
        </p>
      )}
    </div>
  </div>
);

export const Roadmap = () => (
  <div className="hi-bg hi-grain min-h-screen">
    <NavBar />
    <div className="relative z-[2] max-w-5xl mx-auto px-5 py-10 md:py-14">
      <Link to="/" data-testid="roadmap-back" className="hi-cta inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold hi-body bg-white/5 border border-white/10 text-white/80 hover:bg-white/10">
        <ArrowLeft size={16} strokeWidth={2.5} /> Back to Suite
      </Link>

      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mt-8">
        <div className="flex items-center gap-3">
          <span className="grid place-items-center w-11 h-11 rounded-xl bg-[#3b9bff]/15 text-[#3b9bff] border border-[#3b9bff]/30"><Map size={20} /></span>
          <h1 className="hi-serif text-3xl md:text-4xl text-[#f3ead4]">Product Roadmap</h1>
        </div>
        <p className="hi-body text-white/70 mt-3 max-w-2xl">
          An honest look at what's available today and what we're building next. Dates aren't promised — we ship when it's ready and reliable.
        </p>

        <h2 className="hi-serif text-xl text-[#f3ead4] mt-10 mb-4">Available now</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          {LIVE_APPS.map((a) => <Row key={a.id} app={a} live />)}
        </div>

        <h2 className="hi-serif text-xl text-[#f3ead4] mt-10 mb-4">On the roadmap</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          {ROADMAP_APPS.map((a) => <Row key={a.id} app={a} />)}
        </div>

        <div className="hi-card p-6 mt-10 text-center">
          <p className="hi-body text-white/75">Want to be notified when the next app launches?</p>
          <Link to="/#newsletter" className="hi-cta inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold hi-body bg-[#c9a24b] text-[#1a2417] hover:bg-[#e0bb63] mt-4">
            <Rocket size={16} strokeWidth={2.5} /> Get launch updates
          </Link>
        </div>
      </motion.div>
    </div>
    <div className="relative z-[2]"><Footer /></div>
  </div>
);

export default Roadmap;
